import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { safeLocalStorage } from '@/stores/persistStorage';
import { useShallow } from 'zustand/react/shallow';
import { useBatchSessionStore } from '@/stores/batchSessionStore';
import { globalValidationStore } from '@/stores/validationStore';
import { type ValidationFormData, createDefaultFormData } from '@/types/validationFormTypes';
import type { DocumentType, ExtractionMethod, DecimalSeparator } from '@/api/generated-types';
import type { SubmissionResults } from '@/types/submissionTypes';
import type {
  FormDangerousGoodsItem,
  ValidationMetadata,
  Nullish,
  TransactionResponse,
  CuratedArtifactResponse,
  LayoutDetectionResult,
  CargoBbox,
  SaveValidationResponse,
} from '@/api/types';

export interface ExtractedData {
  hasData?: boolean;
  /** Document identity, known at hydrate. `file` carries the bytes and arrives later. */
  filename?: string;
  file?: File;
  fileUrl?: string;
  displayName?: string;
  layoutDetection?: LayoutDetectionResult;
  cargoBbox?: CargoBbox;
}

export interface WorkflowState {
  extractedData: ExtractedData;
  formData: ValidationFormData;
  metadata: ValidationMetadata;
  submissionResults: SubmissionResults;
  lastSavedFormHash: string | null;
  _originalFormData: ValidationFormData | null;
  localTransactionId: string | null;
  hydratedTransactionId: string | null;
  pendingSubstanceRetry: string[] | null;
  expandedCtuIds: Set<string>;

  documentViewerTargetPage: number | null;
  documentViewerTargetSection: number | null;

  _originalMappedGoods: FormDangerousGoodsItem[];

  documentTypeOverride: DocumentType | null;
  extractionMethodOverride: ExtractionMethod | null;
  decimalSeparatorOverride: DecimalSeparator | null;
  modelConfigIdOverride: string | null;
  addOnsOverride: string[] | null;
}

export interface PopulateFromDB {
  transaction: TransactionResponse;
  curated: CuratedArtifactResponse | undefined;
  formData?: ValidationFormData;
  originalMappedGoods: FormDangerousGoodsItem[];
  pdf?: Blob;
  layoutDetection?: LayoutDetectionResult;
  cargoBbox?: CargoBbox;
}

export interface HydrateFromArtifactsParams extends PopulateFromDB {
  savedValidation?: SaveValidationResponse;
}

interface WorkflowActions {
  populateFromDB: (params: PopulateFromDB) => void;
  hydrateFromArtifacts: (params: HydrateFromArtifactsParams) => void;

  setExtractedData: (data: ExtractedData) => void;
  setDocumentBlob: (blob: Blob) => void;
  setFormData: (data: ValidationFormData) => void;
  updateFormData: (partial: Partial<ValidationFormData>) => void;

  setMetadata: (metadata: ValidationMetadata) => void;
  updateMetadata: <K extends keyof ValidationMetadata>(
    field: K,
    value: ValidationMetadata[K]
  ) => void;

  setSubmissionResults: (results: SubmissionResults) => void;
  updateSubmissionResults: (partial: Partial<SubmissionResults>) => void;
  resetSubmissionResults: () => void;

  resetWorkflow: () => void;
  setLastSavedFormHash: (hash: string | null) => void;
  setOriginalFormData: (data: Nullish<ValidationFormData>) => void;

  setLocalTransactionId: (id: string | null) => void;

  markHydrationStale: () => void;

  setPendingSubstanceRetry: (cargoIds: string[] | null) => void;

  beginNewUpload: () => void;

  resetToExtractedData: () => void;

  toggleCtuExpansion: (ctuId: string) => void;
  expandAllCtus: () => void;
  collapseAllCtus: () => void;
  removeCtuExpansion: (ctuId: string) => void;

  navigateToCargoSource: (pageNo: number, sectionIdx: number) => void;
  clearDocumentNavigation: () => void;

  setDocumentTypeOverride: (value: DocumentType | null) => void;
  setExtractionMethodOverride: (value: ExtractionMethod | null) => void;
  setDecimalSeparatorOverride: (value: DecimalSeparator | null) => void;
  setModelConfigIdOverride: (value: string | null) => void;
  setAddOnsOverride: (value: string[] | null) => void;
}

export type WorkflowStore = WorkflowState & WorkflowActions;

// Infer MIME from extension: backend stores DGD (PDF) and DCM (RTF) under the same
// artifact. Forging application/pdf for RTF makes PDFViewer fail to render it.
function toDocumentFile(blob: Blob, filename: string): File {
  const mimeType = /\.rtf$/i.test(filename) ? 'application/rtf' : 'application/pdf';
  return new File([blob], filename, { type: mimeType });
}

function buildPopulateFromDBPatch(params: PopulateFromDB): Partial<WorkflowState> {
  const filename = params.transaction.filename || 'document.pdf';
  const documentFile = params.pdf ? toDocumentFile(params.pdf, filename) : undefined;

  const hasCurated = !!params.curated?.form_data;
  let formData: ValidationFormData;
  let metadata: ValidationMetadata | undefined;

  if (hasCurated && params.curated?.form_data) {
    formData = {
      ...createDefaultFormData(),
      ...params.curated.form_data,
      dangerousGoods: params.curated.form_data.dangerousGoods ?? [],
    };
    metadata = params.curated.metadata;
  } else {
    formData = { ...createDefaultFormData(), ...params.formData };
    metadata = undefined;
  }

  const firstCtuId = formData.cargoTransportUnits?.[0]?.ctuId;

  return {
    extractedData: {
      hasData: true,
      filename,
      file: documentFile,
      displayName: params.transaction.displayName ?? undefined,
      layoutDetection: params.layoutDetection,
      cargoBbox: params.cargoBbox,
    },
    formData,
    metadata: metadata ?? {
      reference: '',
      name: '',
    },
    lastSavedFormHash: null,
    localTransactionId: params.transaction.id ?? null,
    hydratedTransactionId: params.transaction.id ?? null,
    _originalFormData: params.formData,
    _originalMappedGoods: params.originalMappedGoods,
    expandedCtuIds: firstCtuId ? new Set<string>([firstCtuId]) : new Set<string>(),
  };
}

const createInitialState = (): WorkflowState => {
  const formData = createDefaultFormData();
  return {
    extractedData: { hasData: false },
    formData,
    metadata: {
      reference: '',
      name: '',
    },
    submissionResults: {},
    lastSavedFormHash: null,
    _originalFormData: null,
    localTransactionId: null,
    hydratedTransactionId: null,
    pendingSubstanceRetry: null,
    expandedCtuIds: new Set<string>(),
    _originalMappedGoods: [],
    documentViewerTargetPage: null,
    documentViewerTargetSection: null,
    documentTypeOverride: null,
    extractionMethodOverride: null,
    decimalSeparatorOverride: null,
    modelConfigIdOverride: null,
    addOnsOverride: null,
  };
};

/**
 * Middleware: devtools (dev only) + persist. persist covers metadata + transaction id;
 * form data is intentionally NOT persisted — `transactionLoader` re-hydrates it from
 * backend artifacts on every load.
 */
export const useWorkflowStore = create<WorkflowStore>()(
  devtools(
    persist(
      (set) => ({
        ...createInitialState(),

        populateFromDB: (params) =>
          set(() => buildPopulateFromDBPatch(params), false, 'populateFromDB'),

        hydrateFromArtifacts: (params) =>
          set(
            () => {
              const base = buildPopulateFromDBPatch(params);
              const submissionResults = params.savedValidation
                ? {
                    savedValidationUrl: params.savedValidation.validationUrl ?? null,
                    savedValidationId: params.savedValidation.id,
                    savedAt: params.savedValidation.timestamp,
                  }
                : {};
              return {
                ...base,
                submissionResults,
                // Cleared before the plan runs, so a load can only ever queue its own retry.
                pendingSubstanceRetry: null,
              };
            },
            false,
            'hydrateFromArtifacts'
          ),

        setExtractedData: (data) => set({ extractedData: data }, false, 'setExtractedData'),
        setDocumentBlob: (blob) =>
          set(
            (state) => ({
              extractedData: {
                ...state.extractedData,
                file: toDocumentFile(blob, state.extractedData.filename || 'document.pdf'),
              },
            }),
            false,
            'setDocumentBlob'
          ),

        setFormData: (data) => set({ formData: data }, false, 'setFormData'),

        updateFormData: (partial) =>
          set(
            (state) => ({
              formData: { ...state.formData, ...partial },
            }),
            false,
            'updateFormData'
          ),

        setMetadata: (metadata) => set({ metadata }, false, 'setMetadata'),

        updateMetadata: (field, value) =>
          set(
            (state) => ({
              metadata: { ...state.metadata, [field]: value },
            }),
            false,
            { type: 'updateMetadata', field }
          ),

        setSubmissionResults: (results) =>
          set({ submissionResults: results }, false, 'setSubmissionResults'),

        updateSubmissionResults: (partial) =>
          set(
            (state) => ({
              submissionResults: { ...state.submissionResults, ...partial },
            }),
            false,
            'updateSubmissionResults'
          ),

        resetSubmissionResults: () =>
          set({ submissionResults: {} }, false, 'resetSubmissionResults'),

        resetWorkflow: () => set(createInitialState(), false, 'resetWorkflow'),

        setLastSavedFormHash: (hash) =>
          set(
            (state) => (state.lastSavedFormHash === hash ? state : { lastSavedFormHash: hash }),
            false,
            'setLastSavedFormHash'
          ),

        setOriginalFormData: (data) =>
          set({ _originalFormData: data ?? null }, false, 'setOriginalFormData'),

        setLocalTransactionId: (id) =>
          set({ localTransactionId: id }, false, 'setLocalTransactionId'),

        markHydrationStale: () => set({ hydratedTransactionId: null }, false, 'markHydrationStale'),

        setPendingSubstanceRetry: (cargoIds) =>
          set({ pendingSubstanceRetry: cargoIds }, false, 'setPendingSubstanceRetry'),

        beginNewUpload: () => {
          set(
            {
              extractedData: {
                hasData: false,
                filename: undefined,
                file: undefined,
                fileUrl: undefined,
              },
              formData: createDefaultFormData(),
              submissionResults: {},
              lastSavedFormHash: null,
              localTransactionId: null,
              hydratedTransactionId: null,
              pendingSubstanceRetry: null,
            },
            false,
            'beginNewUpload'
          );
          useBatchSessionStore.getState().clearBatchSession();
          globalValidationStore.getState().resetValidation();
        },

        resetToExtractedData: () =>
          set(
            (state) => {
              if (!state._originalFormData) return state;
              const formData = { ...createDefaultFormData(), ...state._originalFormData };
              return {
                formData,
                lastSavedFormHash: null,
                submissionResults: {},
                _originalMappedGoods: formData.dangerousGoods.map((item) => ({ ...item })),
              };
            },
            false,
            'resetToExtractedData'
          ),

        toggleCtuExpansion: (ctuId) =>
          set(
            (state) => {
              const newSet = new Set(state.expandedCtuIds);
              if (newSet.has(ctuId)) {
                newSet.delete(ctuId);
              } else {
                newSet.add(ctuId);
              }
              return { expandedCtuIds: newSet };
            },
            false,
            'toggleCtuExpansion'
          ),

        expandAllCtus: () =>
          set(
            (state) => {
              const ctuIds =
                state.formData.cargoTransportUnits?.map((ctu) => ctu.ctuId ?? '') ?? [];
              return { expandedCtuIds: new Set(ctuIds) };
            },
            false,
            'expandAllCtus'
          ),

        collapseAllCtus: () => set({ expandedCtuIds: new Set() }, false, 'collapseAllCtus'),

        removeCtuExpansion: (ctuId) =>
          set(
            (state) => {
              if (!state.expandedCtuIds.has(ctuId)) return state;
              const next = new Set(state.expandedCtuIds);
              next.delete(ctuId);
              return { expandedCtuIds: next };
            },
            false,
            'removeCtuExpansion'
          ),

        navigateToCargoSource: (pageNo, sectionIdx) =>
          set(
            (state) => {
              if (pageNo < 0 || sectionIdx < 0) return state;
              return {
                documentViewerTargetPage: pageNo,
                documentViewerTargetSection: sectionIdx,
              };
            },
            false,
            'navigateToCargoSource'
          ),

        clearDocumentNavigation: () =>
          set(
            {
              documentViewerTargetPage: null,
              documentViewerTargetSection: null,
            },
            false,
            'clearDocumentNavigation'
          ),

        setDocumentTypeOverride: (value) =>
          set({ documentTypeOverride: value }, false, 'setDocumentTypeOverride'),
        setExtractionMethodOverride: (value) =>
          set({ extractionMethodOverride: value }, false, 'setExtractionMethodOverride'),
        setDecimalSeparatorOverride: (value) =>
          set({ decimalSeparatorOverride: value }, false, 'setDecimalSeparatorOverride'),
        setModelConfigIdOverride: (value) =>
          set({ modelConfigIdOverride: value }, false, 'setModelConfigIdOverride'),
        setAddOnsOverride: (value) => set({ addOnsOverride: value }, false, 'setAddOnsOverride'),
      }),
      {
        name: 'workflow-storage',
        storage: createJSONStorage(() => safeLocalStorage),
        partialize: (state) => ({
          metadata: state.metadata,
          submissionResults: {
            substanceCount: state.submissionResults.substanceCount,
            cargoUnitsCount: state.submissionResults.cargoUnitsCount,
            savedValidationUrl: state.submissionResults.savedValidationUrl,
            savedValidationId: state.submissionResults.savedValidationId,
            savedAt: state.submissionResults.savedAt,
          },
          lastSavedFormHash: state.lastSavedFormHash,
          localTransactionId: state.localTransactionId,
        }),
      }
    ),
    {
      name: 'WorkflowStore',
      enabled: import.meta.env.DEV,
    }
  )
);

export const useOriginalExtractedGoods = () =>
  useWorkflowStore((state) => state._originalMappedGoods);

export const useExtractedData = () => useWorkflowStore((state) => state.extractedData);
export const useFormData = () => useWorkflowStore((state) => state.formData);
export const useMetadata = () => useWorkflowStore((state) => state.metadata);
export const useSubmissionResults = () => useWorkflowStore((state) => state.submissionResults);
export const useValidationRequest = () =>
  useWorkflowStore((state) => state.submissionResults.validationRequest ?? null);
export const useCheckResults = () =>
  useWorkflowStore((state) => state.submissionResults.checkResults);
export const useSavedValidationUrl = () =>
  useWorkflowStore((state) => state.submissionResults.savedValidationUrl ?? null);
export const useSavedValidationId = () =>
  useWorkflowStore((state) => state.submissionResults.savedValidationId ?? null);
export const useOriginalFormData = () => useWorkflowStore((state) => state._originalFormData);
export const useLocalTransactionId = () => useWorkflowStore((state) => state.localTransactionId);
// Selection state lives in the URL — see useDGSelection.
export const useExpandedCtuIds = () => useWorkflowStore((state) => state.expandedCtuIds);
export const useIsCtuExpanded = (ctuId: string) =>
  useWorkflowStore((state) => state.expandedCtuIds.has(ctuId));

export const useDocumentViewerTargetPage = () =>
  useWorkflowStore((state) => state.documentViewerTargetPage);
export const useDocumentViewerTargetSection = () =>
  useWorkflowStore((state) => state.documentViewerTargetSection);

// Extraction config override hooks
export const useDocumentTypeOverride = () =>
  useWorkflowStore((state) => state.documentTypeOverride);
export const useExtractionMethodOverride = () =>
  useWorkflowStore((state) => state.extractionMethodOverride);
export const useDecimalSeparatorOverride = () =>
  useWorkflowStore((state) => state.decimalSeparatorOverride);
export const useModelConfigIdOverride = () =>
  useWorkflowStore((state) => state.modelConfigIdOverride);
export const useAddOnsOverride = () => useWorkflowStore((state) => state.addOnsOverride);
export const useExtractionConfigActions = () =>
  useWorkflowStore(
    useShallow((s) => ({
      setDocumentTypeOverride: s.setDocumentTypeOverride,
      setExtractionMethodOverride: s.setExtractionMethodOverride,
      setDecimalSeparatorOverride: s.setDecimalSeparatorOverride,
      setModelConfigIdOverride: s.setModelConfigIdOverride,
      setAddOnsOverride: s.setAddOnsOverride,
    }))
  );

export const useWorkflowDataActions = () =>
  useWorkflowStore(
    useShallow((s) => ({
      setExtractedData: s.setExtractedData,
      setFormData: s.setFormData,
      updateFormData: s.updateFormData,
      setMetadata: s.setMetadata,
      updateMetadata: s.updateMetadata,
      setOriginalFormData: s.setOriginalFormData,
      setLastSavedFormHash: s.setLastSavedFormHash,
    }))
  );

export const useWorkflowSubmissionActions = () =>
  useWorkflowStore(
    useShallow((s) => ({
      setSubmissionResults: s.setSubmissionResults,
      updateSubmissionResults: s.updateSubmissionResults,
      resetSubmissionResults: s.resetSubmissionResults,
    }))
  );

export const useWorkflowLifecycleActions = () =>
  useWorkflowStore(
    useShallow((s) => ({
      populateFromDB: s.populateFromDB,
      resetWorkflow: s.resetWorkflow,
      resetToExtractedData: s.resetToExtractedData,
      setLocalTransactionId: s.setLocalTransactionId,
      beginNewUpload: s.beginNewUpload,
    }))
  );

export const useWorkflowCtuActions = () =>
  useWorkflowStore(
    useShallow((s) => ({
      toggleCtuExpansion: s.toggleCtuExpansion,
      expandAllCtus: s.expandAllCtus,
      collapseAllCtus: s.collapseAllCtus,
      removeCtuExpansion: s.removeCtuExpansion,
    }))
  );

export const useWorkflowNavActions = () =>
  useWorkflowStore(
    useShallow((s) => ({
      navigateToCargoSource: s.navigateToCargoSource,
      clearDocumentNavigation: s.clearDocumentNavigation,
    }))
  );

export const useDcmPreviewData = () =>
  useWorkflowStore(
    useShallow((s) => ({
      documentType: s.formData.documentType ?? null,
      vesselName: s.formData.vesselName ?? null,
      voyageNumber: s.formData.voyageNumber ?? null,
      dcmItinerary: s.formData.dcm?.itinerary ?? null,
      cargoTransportUnits: s.formData.cargoTransportUnits,
      dangerousGoods: s.formData.dangerousGoods,
    }))
  );

export const getWorkflowState = () => useWorkflowStore.getState();

export default useWorkflowStore;
